import { get } from "lodash"
import { Creators as UpdaterCreators } from "model/hookUpdater"
import { getRole } from "helpers/permissions/roles"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as modelCreators } from "model/data"
import { Creators as notificationCreators } from "modules/notifications/model"
import { getIsOpPro } from "modules/account-root/selectors"
import { getMembers, getOverProMemberLimitMessage, getHasEzeIntegration, getRestrictedFeatures, getMemberFeatures, getMemberFeaturesLoading, getAccountFeatures } from "./selectors"
import { getTabs } from "modules/tokens/selectors"

// Styles are loaded here
import "./styles.scss"
import withOutletContext from "hocs/withOutletContext"

export default withOutletContext(createModule({
  // The view is used to render the module
  view,
  // Used to pull some props used to load data.
  externalProps: (state) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    userId: state.user.id,
    isSuperuser: state.user.isSuperuser,
  }),
  // Declares a number of props required before the screen attemps to load.
  required: ["account"],
  // Declares a list of resources that needs to be loaded
  data: [{
    // Identifies the resource for retrival later
    resource: "members",

    // `passthrough` makes the screen not wait for data to be loaded.
    // If set to `true`, screen needs to handle error and loading states manually.
    // If not set, we will automatically display a loading or error state
    passthrough: true,

    // `as` can be set to either rename the resource prop (if a string is provided)
    // or to not pass the resource as a prop (normally because it's modified by a selector)
    // as: null,

    // `url` can be either a function returning, null, a string or an object.
    // It receives the external props as parameters.
    // If it returns null, it doesn't load anything
    // If it returns a string, it makes a GET call to that URL
    // It re-runs the query if the string changes for a gived `resource`
    // If it returns an object, it makes a GraphQL call.
    // Valid object keys for the object are:
    //   - id (optional): re-runs the query if the id changes
    //   - query (required): the query to run
    //   - variables (optional): the variables provided to the GraphQL call
    url: ({ account }) => `/api/${account}/members`,
  }, {
    resource: "ezeMetadata",
    url: ({ account }) => ({
      id: `ezeMetadata-${account}`,
      url: `/api/${account}/partners/meta?partnerType=eze`,
    }),
  }, {
    resource: "features",
    url: ({ account, isSuperuser }) => !isSuperuser ? null : ({
      id: `features-${account}`,
      url: "/api/admin/features",
    }),
  }, {
    resource: "accountFeatures",
    url: ({ account, accountId, isSuperuser }) => !isSuperuser ? null : ({
      id: `features-${account}`,
      url: `/api/admin/accounts/${accountId}/features`,
    }),
  }],
  // `mapStateToProps` does exactly what it says. This is the main way to pass in props to the view.
  // These props are added to the external props.
  // If data needs to be reformated, it should go through a selector.
  // Newer screens use a useMemo hook instead
  mapStateToProps: (state, props) => ({
    role: getRole(state),
    members: getMembers(state, props),
    isSuperuser: get(state, "user.isSuperuser"),
    isOpPro: getIsOpPro(state),
    tabs: getTabs(state, props),
    overProMemberLimitMessage: getOverProMemberLimitMessage(state, props),
    hasEzeIntegration: getHasEzeIntegration(state),
    features: getRestrictedFeatures(state, props),
    memberFeatures: getMemberFeatures(state, props),
    isMemberFeaturesLoading: getMemberFeaturesLoading(state, props),
    accountFeatures: getAccountFeatures(state, props),
  }),
  // `actions` provides external actions to the screen. The actions are normally declared in a model file
  actions: {
    ...UpdaterCreators,
    ...Creators,
    resetCollections: modelCreators.resetCollections,
    addNotification: notificationCreators.add,
  },
}))
